import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Repeatable } from '../components/Field';
import { LayoutEditBar } from '../components/LayoutEditor';
import SubFieldRowsEditor from '../components/SubFieldRowsEditor';
import RichText from '../components/RichText';
import CandidateBanner from '../components/CandidateBanner';
import CopyOthers, { INPUT_ID as COPY_OTHERS_INPUT } from '../components/CopyOthers';
import { sanitizeSubFieldRows } from '../lib/fieldLayout';
import { CallStatus, useCallable } from '../lib/useCallable';
import { useDoc } from '../lib/useFirestore';
import {
  SUBJECT_SUFFIX_MAX,
  conflictCheckSubject,
  conflictsPartyFields,
  normalizeConflictsPage,
  validateConflictsSubmission,
} from '../lib/conflictsPageSettings';

/**
 * One inline text/textarea field, editable in place when `editingLayout` is on.
 *
 * Displayed copy goes through RichText, so an administrator can put a word in bold with **two
 * asterisks** and start a line where they mean to. While editing they see the raw text, because
 * what is being edited is the source, not a preview of it.
 */
function EditableCopy({ editingLayout, tag: Tag = 'span', className = '', value, onChange, multiline = false }) {
  if (!editingLayout)
    return (
      <Tag className={className}>
        <RichText>{value}</RichText>
      </Tag>
    );
  const Control = multiline ? 'textarea' : 'input';
  return (
    <Control
      className={`block w-full rounded border border-dashed border-amber-400 bg-amber-50/40 px-2 py-1 dark:border-amber-600 dark:bg-amber-950/20 ${className}`}
      value={value}
      rows={multiline ? 3 : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/**
 * A deliberately isolated conflict-check request surface. It uses the same party-row
 * component as a full intake without writing a matter or exposing the main-app shell.
 */
export default function ConflictsStandalone() {
  const { user, displayName, isAdmin } = useAuth();
  const { run, busy, error } = useCallable();
  const layoutCall = useCallable();
  // Admin-editable copy and options (Admin -> Conflicts page). A missing document just
  // yields the shipped defaults, so this page never depends on the doc existing.
  const { data: storedPage } = useDoc('settings/conflictsPage');
  const page = useMemo(() => normalizeConflictsPage(storedPage), [storedPage]);
  const [parties, setParties] = useState([]);
  const [description, setDescription] = useState('');
  // Added to the end of the email subject, e.g. the plaintiff's last name. Optional.
  const [subjectSuffix, setSubjectSuffix] = useState('');
  /**
   * Who else is copied on this request, beyond the Conflicts team and the submitter.
   *
   * Held here rather than on a document: a conflict check is submitted in one sitting and there
   * is no draft to carry it. The list is remembered for the person's NEXT request, which is the
   * whole point — somebody who copies their secretary does so every time.
   */
  const [ccEmails, setCcEmails] = useState([]);
  /**
   * Whatever is still typed in the Copy others box and not yet a name on the list.
   *
   * A submit with text left there is refused rather than sent without it. That is how the first
   * report of this went wrong: somebody typed a colleague's name, never picked it from the list,
   * and submitted. The request went out without that person, and nothing said so.
   */
  const [ccPending, setCcPending] = useState('');
  const [ccBlocked, setCcBlocked] = useState(false);
  const onCcPendingChange = (text) => {
    setCcPending(text);
    if (!text) setCcBlocked(false);
  };
  const ccMessage =
    ccBlocked && ccPending
      ? `“${ccPending}” is still in the Copy others box and has not been added. Pick the person from the list, or clear the box to send without them.`
      : '';
  const [validationError, setValidationError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [layoutDraft, setLayoutDraft] = useState(null);
  const editingLayout = isAdmin && layoutDraft !== null;
  const view = editingLayout ? layoutDraft : page;
  const setCopy = (key) => (value) => setLayoutDraft((prev) => ({ ...prev, [key]: value }));

  const partyField = useMemo(
    () => ({
      path: 'parties',
      label: view.partiesTitle,
      type: 'repeatable',
      addLabel: 'Add name or entity',
      fields: conflictsPartyFields(view),
    }),
    [view]
  );

  const saveLayout = async () => {
    const cleaned = {
      ...layoutDraft,
      partyFields: sanitizeSubFieldRows(layoutDraft.partyFields).filter((f) => f.path !== 'name'),
    };
    const result = await layoutCall.run('adminUpdateSettings', { docId: 'conflictsPage', value: cleaned });
    if (result?.ok) setLayoutDraft(null);
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${page.title} | AALRR`;
    return () => {
      document.title = previousTitle;
    };
  }, [page.title]);

  const submitter = useMemo(
    () => ({
      name: displayName || user?.email || '',
      email: user?.email || '',
    }),
    [displayName, user?.email]
  );

  const submit = async (event) => {
    event.preventDefault();
    const problem = validateConflictsSubmission(storedPage, { parties, description });
    if (problem) {
      setValidationError(problem);
      return;
    }
    if (ccPending) {
      setValidationError('');
      setCcBlocked(true);
      document.getElementById(COPY_OTHERS_INPUT)?.focus();
      return;
    }

    setValidationError('');
    const result = await run('submitConflictCheck', { parties, description, ccEmails, subjectSuffix });
    if (result?.ok) {
      setSubmitted(true);
      setParties([]);
      setDescription('');
      setSubjectSuffix('');
    }
  };

  return (
    <main className="conflicts-request min-h-full bg-slate-100 dark:bg-slate-950">
      {/*
        The same frame as the candidate pages: firm mark, title, version, sign out. This page used
        to carry its title as a heading inside the card and nothing else, which made a tool that
        shares a deployment with the others feel like a different application.
      */}
      <CandidateBanner title={view.title} width="max-w-3xl" showVersion showSignOut hideVersionOnPhone />
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-8 sm:px-6 sm:py-12">
        {editingLayout && (
          <LayoutEditBar
            dirty
            busy={layoutCall.busy}
            error={layoutCall.error}
            onSave={saveLayout}
            onDiscard={() => setLayoutDraft(null)}
          />
        )}

        <header className="border-b border-slate-300 pb-5 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {/*
                The eyebrow and the title are shown here only while the layout is being edited:
                the title is in the banner above for everybody else, and printing it twice was the
                obvious way to make this page look untidy the moment the banner arrived.
              */}
              {editingLayout && (
                <>
                  <EditableCopy
                    editingLayout
                    tag="p"
                    className="text-xs font-semibold tracking-[0.18em] text-firm-700 uppercase dark:text-firm-100"
                    value={view.eyebrow}
                    onChange={setCopy('eyebrow')}
                  />
                  <EditableCopy
                    editingLayout
                    tag="h1"
                    className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white"
                    value={view.title}
                    onChange={setCopy('title')}
                  />
                </>
              )}
              <EditableCopy
                editingLayout={editingLayout}
                tag="p"
                className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300"
                value={view.intro}
                onChange={setCopy('intro')}
                multiline
              />
            </div>
            {isAdmin && !editingLayout && (
              <button
                type="button"
                onClick={() => setLayoutDraft(page)}
                className="shrink-0 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-white dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Edit layout
              </button>
            )}
          </div>
        </header>

        {submitted && !editingLayout ? (
          <section
            className="mt-6 rounded border border-emerald-300 bg-emerald-50 p-6 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
            aria-live="polite"
          >
            <h2 className="text-lg font-semibold">{page.submittedTitle}</h2>
            <p className="mt-1 text-sm">{page.submittedBody}</p>
            <button
              type="button"
              className="mt-4 rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-800 focus:outline-none focus:ring-2 focus:ring-firm-500 focus:ring-offset-2 dark:bg-firm-500 dark:hover:bg-firm-400 dark:focus:ring-offset-slate-950"
              onClick={() => setSubmitted(false)}
            >
              {page.submitAnotherLabel}
            </button>
          </section>
        ) : (
          <form className="space-y-7" onSubmit={submit}>
            <section className="rounded bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:p-6">
              <EditableCopy
                editingLayout={editingLayout}
                tag="h2"
                className="text-base font-semibold text-slate-900 dark:text-white"
                value={view.requestingPartyTitle}
                onChange={setCopy('requestingPartyTitle')}
              />
              <EditableCopy
                editingLayout={editingLayout}
                tag="p"
                className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                value={view.requestingPartyHelp}
                onChange={setCopy('requestingPartyHelp')}
                multiline
              />
              <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Name
                  </dt>
                  <dd className="mt-1 break-words text-sm text-slate-900 dark:text-white">{submitter.name}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Email
                  </dt>
                  <dd className="mt-1 break-words text-sm text-slate-900 dark:text-white">{submitter.email}</dd>
                </div>
              </dl>
            </section>

            {/*
              The email subject line. The box holds only the requester's own addition, and the
              line under it shows the whole subject as it will be sent, so what the box does is
              visible before anything is submitted. The server builds the real subject
              (functions/src/conflicts/subject.js); the preview is held to it by a test.
            */}
            <section className="rounded bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:p-6">
              <label htmlFor="conflict-subject" className="block text-base font-semibold text-slate-900 dark:text-white">
                <EditableCopy
                  editingLayout={editingLayout}
                  value={view.subjectLabel}
                  onChange={setCopy('subjectLabel')}
                />{' '}
                <span className="font-normal text-slate-500 dark:text-slate-400">(optional)</span>
              </label>
              <EditableCopy
                editingLayout={editingLayout}
                tag="p"
                className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                value={view.subjectHelp}
                onChange={setCopy('subjectHelp')}
                multiline
              />
              {!editingLayout && (
                <>
                  <input
                    id="conflict-subject"
                    type="text"
                    className="mt-4 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-firm-500 focus:outline-none focus:ring-1 focus:ring-firm-500 disabled:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:disabled:bg-slate-800"
                    maxLength={SUBJECT_SUFFIX_MAX}
                    value={subjectSuffix}
                    disabled={busy}
                    onChange={(event) => setSubjectSuffix(event.target.value)}
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Subject:{' '}
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {conflictCheckSubject({
                        count: parties.filter((party) => typeof party?.name === 'string' && party.name.trim()).length,
                        submitterName: submitter.name,
                        suffix: subjectSuffix,
                      })}
                    </span>
                  </p>
                </>
              )}
            </section>

            <section className="rounded bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:p-6">
              <EditableCopy
                editingLayout={editingLayout}
                tag="h2"
                className="text-base font-semibold text-slate-900 dark:text-white"
                value={view.partiesTitle}
                onChange={setCopy('partiesTitle')}
              />
              <EditableCopy
                editingLayout={editingLayout}
                tag="p"
                className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                value={view.partiesHelp}
                onChange={setCopy('partiesHelp')}
                multiline
              />
              <div className="mt-4">
                <Repeatable
                  field={partyField}
                  value={parties}
                  onChange={(next) => {
                    setParties(next);
                    if (validationError) setValidationError('');
                  }}
                  disabled={busy}
                  config={{}}
                />
              </div>
              {editingLayout && (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-700 dark:bg-amber-950/20">
                  <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
                    Every entry always has a <strong>Name</strong> field. Add columns below only
                    if Conflicts needs more per entry (entity type, role, notes, ...).
                  </p>
                  <SubFieldRowsEditor
                    fields={layoutDraft.partyFields}
                    onChange={setCopy('partyFields')}
                    legend="Extra columns (beyond Name)"
                  />
                </div>
              )}
            </section>

            {(view.descriptionEnabled || editingLayout) && (
            <section className="rounded bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:p-6">
              {editingLayout && (
                <div className="mb-3 flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-200">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={layoutDraft.descriptionEnabled}
                      onChange={(event) => setCopy('descriptionEnabled')(event.target.checked)}
                    />
                    Show this field
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={layoutDraft.descriptionRequired}
                      disabled={!layoutDraft.descriptionEnabled}
                      onChange={(event) => setCopy('descriptionRequired')(event.target.checked)}
                    />
                    Require it before submitting
                  </label>
                </div>
              )}
              <label htmlFor="conflict-description" className="block text-base font-semibold text-slate-900 dark:text-white">
                <EditableCopy
                  editingLayout={editingLayout}
                  value={view.descriptionLabel}
                  onChange={setCopy('descriptionLabel')}
                />
                {view.descriptionRequired && !editingLayout && (
                  <span className="ml-1 text-red-600 dark:text-red-400" title="Required">*</span>
                )}
              </label>
              <EditableCopy
                editingLayout={editingLayout}
                tag="p"
                className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                value={view.descriptionHelp}
                onChange={setCopy('descriptionHelp')}
                multiline
              />
              {!editingLayout && view.descriptionEnabled && (
                <textarea
                  id="conflict-description"
                  className="mt-4 min-h-28 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-firm-500 focus:outline-none focus:ring-1 focus:ring-firm-500 disabled:bg-slate-100 dark:border-slate-600 dark:disabled:bg-slate-800"
                  rows={5}
                  maxLength={5000}
                  value={description}
                  disabled={busy}
                  onChange={(event) => setDescription(event.target.value)}
                />
              )}
            </section>
            )}

            {/*
              A field of its own, above the buttons. It was a button in the submit row with a
              count badge; who is being copied is the thing that should be readable without
              clicking, since it is the only part of this form that sends the request onward.
            */}
            {!editingLayout && (
              <CopyOthers
                value={ccEmails}
                onChange={setCcEmails}
                onPendingChange={onCcPendingChange}
                disabled={busy}
              />
            )}

            {!editingLayout && (validationError || ccMessage || error) && (
              <p className="text-sm text-red-700 dark:text-red-300" role="alert">
                {validationError || ccMessage || error}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-slate-300 pt-5 dark:border-slate-700">
              {editingLayout && <CallStatus busy={layoutCall.busy} error={layoutCall.error} saved={layoutCall.saved} />}
              {editingLayout ? (
                <EditableCopy
                  editingLayout
                  className="max-w-xs text-right"
                  value={view.submitLabel}
                  onChange={setCopy('submitLabel')}
                />
              ) : (
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded bg-firm-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-firm-800 focus:outline-none focus:ring-2 focus:ring-firm-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-firm-500 dark:hover:bg-firm-400 dark:focus:ring-offset-slate-950"
                >
                  {busy ? 'Submitting…' : page.submitLabel}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
